import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  AttendanceStats,
  attendanceRecords,
} from '../../../../models/attendance.school.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolAttendanceService } from '../../../../services/attendance.school.service';
import * as lessonActions from '../../../../state/actions/grade-lesson.school.actions';
import * as gradeActions from '../../../../state/actions/grade.school.actions';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { ListAssignmentsComponent } from '../../assignments/list-assignments/list-assignments.component';
import { DlgUpsertAwardComponent } from '../../awards/dlg-upsert-award/dlg-upsert-award.component';
import { ListAwardsComponent } from '../../awards/list-awards/list-awards.component';

@Component({
  selector: 'app-detail-student',
  standalone: true,
  imports: [
    AsyncPipe,
    MomentPipe,
    RouterLink,
    NgxSkeletonLoaderModule,
    BtnBackComponent,
    BtnActionComponent,
    BtnMoreComponent,
    BtnRouteComponent,
    ListAwardsComponent,
    ListAssignmentsComponent,
  ],
  templateUrl: './detail-student.component.html',
  styleUrl: './detail-student.component.css',
})
export class DetailStudentComponent implements OnInit, OnDestroy {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private dialog: MatDialog = inject(MatDialog);
  store: Store<AppState> = inject(Store<AppState>);
  attendanceService: SchoolAttendanceService = inject(SchoolAttendanceService);
  logger: NGXLogger = inject(NGXLogger);

  activeTab: string = 'lessons';

  lessonsHeader: string[] = ['Lesson Name', 'Created Date'];

  groupId: string = this.activatedRoute.snapshot.paramMap.get('id')!;
  studentId: string = this.activatedRoute.snapshot.paramMap.get('studentId')!;
  academicYear: number | undefined = undefined;

  myRoles$: Observable<any> = new Observable();

  student: SchoolMember | null | undefined;
  lessons: GradeLesson[] | null | undefined;
  grade: Grade | null | undefined;

  loadingLessons$: Observable<boolean> = new Observable();

  group: Group | null | undefined;
  user: UserModel | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;

  exit$ = new Subject<void>();

  attendanceStats: AttendanceStats | undefined;
  attendanceRecords = attendanceRecords;

  constructor() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id')!;
        this.studentId = params.get('studentId')!;
      });

    this.activatedRoute.fragment
      .pipe(takeUntil(this.exit$))
      .subscribe((fragment) => {
        this.activeTab =
          fragment != undefined && fragment != '' ? fragment : 'lessons';
        this.setActiveTab(this.activeTab);
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.store
      .select(memberSelector.getSchoolMemberById(this.studentId, this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((student) => {
        this.student = student;

        if (this.student?.gradeId && this.student?.gradeId !== 'unassigned') {
          this.store
            .select(
              gradeSelector.getGradeById({
                gradeId: this.student?.gradeId,
                groupId: this.groupId,
                academicYear:
                  this.academicYear ?? this.group?.academicYear ?? null,
              }),
            )
            .pipe(takeUntil(this.exit$))
            .subscribe((grade) => {
              this.grade = grade;

              if (grade == null) {
                this.store.dispatch(
                  gradeActions.getGrade({
                    groupId: this.groupId,
                    gradeYear: this.academicYear ?? this.group?.academicYear!,
                    gradeId: this.student?.gradeId!,
                  }),
                );
              }
            });

          this.store
            .select(
              lessonSelector.getGradeLessonsByGradeId({
                gradeId: this.student?.gradeId,
                groupId: this.groupId,
                academicYear: this.group?.academicYear,
                lessonStatus: [
                  GradeLessonStatus.open,
                  GradeLessonStatus.closed,
                ],
                publishBy: true,
              }),
            )
            .pipe(takeUntil(this.exit$))
            .subscribe((lessons) => {
              this.lessons = lessons;
            });
        }
      });

    this.loadingLessons$ = this.store.select(lessonSelector.loading);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }

  ngOnInit(): void {
    if (this.student == null) {
      this.store.dispatch(
        memberActions.getMember({
          groupId: this.groupId,
          memberId: this.studentId,
        }),
      );
    }

    if ((this.lessons ?? [])?.length === 0) {
      this.store.dispatch(
        lessonActions.getGradeLessons({
          groupId: this.groupId,
          gradeId: this.student?.gradeId!,
          status: [GradeLessonStatus.open, GradeLessonStatus.closed],
          publishBy: true,
          academicYear: this.group?.academicYear!,
        }),
      );
    }

    this.store
      .select((store) => store.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.getAttendance();
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  getAttendance() {
    const params = {
      groupId: this.groupId,
      academicYear: this.academicYear ?? this.group?.academicYear!,
      studentId: this.student?.memberId!,
    };
    this.logger.debug('DetailStudentComponent :: getAttendance', params);
    this.attendanceService
      .getStudentAttendance(params)
      .then((result) => {
        this.logger.debug(
          'DetailStudentComponent :: getAttendance result',
          result,
        );
        this.attendanceStats = result;
      })
      .catch((error) => {
        this.logger.error(
          'DetailStudentComponent :: getAttendance failed',
          error,
        );
      });
  }

  onNewAward() {
    if (!this.groupId || !this.grade?.id) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Add Certificate',
      description: `Add a new certificate for ${this.student?.name}`,
      action: 'Add',
      groupId: this.groupId,
      academicYear: this.group?.academicYear,
      studentId: this.student?.memberId,
      studentName: this.student?.name,
      studentPhotoUrl: this.student?.photoURL,
    };

    const dialogRef = this.dialog.open(DlgUpsertAwardComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      // console.log('onNewAward Completed');
      return;
    });
  }

  onEditProfile() {}

  addMoreLessons() {
    this.store.dispatch(
      lessonActions.getGradeLessons({
        groupId: this.groupId,
        gradeId: this.student?.gradeId!,
        academicYear: this.group?.academicYear!,
        status: [GradeLessonStatus.open, GradeLessonStatus.closed],
        publishBy: true,
        lastRecord:
          (this.lessons ?? []).length > 0
            ? this.lessons![this.lessons!.length - 1]?.modified
            : undefined,
      }),
    );
  }

  setActiveTab(tab: string) {
    if (
      tab === 'overview' &&
      this.activeTab != 'overview' &&
      this.attendanceStats === undefined
    ) {
      this.getAttendance();
    }
    this.activeTab = tab;
  }

  get isStudentOrParent(): boolean {
    if (!this.user?.uid) return false;
    const isStudent =
      this.user.uid === this.studentId ||
      (this.student?.memberId != null && this.user.uid === this.student.memberId);
    const isParent =
      (this.student?.parentId != null && this.student.parentId === this.user.uid) ||
      this.activatedRoute.snapshot.paramMap.get('parentId') === this.user.uid;
    return Boolean(isStudent || isParent);
  }
}
